import React, { useState, useEffect } from "react";
import { Link as MuiLink, Box, Button, Card, CardContent, CardHeader, LinearProgress, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { getProxyApplication } from "../clients/FixieApiClient";
import ExampleCurl from "../components/ExampleCurl";
import FormatBandwidth from "../components/FormatBandwidth";
import FormatNumber from "../components/FormatNumber";
import DashboardPage from "../layouts/DashboardPage";
import { ProxyApplicationDetails } from "../models";
import ErrorState from "../components/ErrorState";

const DetailsBody = (props: { proxyApplication: ProxyApplicationDetails }) => {
  const { proxyApplication } = props;
  const requestUsage = proxyApplication.usage.requests / proxyApplication.plan.requestsLimit * 100;
  const bandwidthUsage = proxyApplication.usage.bandwidth / proxyApplication.plan.bandwidthLimit * 100;
  const documentationUrl = proxyApplication.proxyType === 'http' ? 'https://usefixie.com/documentation/' : 'https://usefixie.com/documentation/socks/';
  return (
    <Box sx={{ m: 2 }}>
      <Box>
        <Card sx={{ marginBottom: '1rem' }}>
          <CardHeader title="Usage" />
          <CardContent>
            <Typography variant="h6">Data Transfer</Typography>
            <LinearProgress variant="determinate" value={bandwidthUsage} sx={{ height: '2rem' }} />
            <Typography variant="caption">
              <FormatBandwidth bytes={proxyApplication.usage.bandwidth} /> of <FormatBandwidth bytes={proxyApplication.plan.bandwidthLimit} /> month data transfer used
            </Typography>
            <Typography variant="h6" sx={{ marginTop: '0.5rem' }} >Requests</Typography>
            <LinearProgress variant="determinate" value={requestUsage} sx={{ height: '2rem' }} />
            <Typography variant="caption">
              <FormatNumber value={proxyApplication.usage.requests} /> of <FormatNumber value={proxyApplication.plan.requestsLimit} /> monthly requests used
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ marginBottom: '1rem' }}>
          <CardHeader title="Details" />
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Proxy URL</TableCell>
                  <TableCell>{proxyApplication.proxyUrl}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Outbound IPs</TableCell>
                  <TableCell>{proxyApplication.ips.map((ip: string) => (<Box key={ip}>{ip}</Box>))}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Proxy Type</TableCell>
                  <TableCell>{proxyApplication.proxyType.toUpperCase()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Plan Name</TableCell>
                  <TableCell>{proxyApplication.plan.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly Data Transfer Limit</TableCell>
                  <TableCell><FormatBandwidth bytes={proxyApplication.plan.bandwidthLimit} /></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Monthly Request Volume Limit</TableCell>
                  <TableCell><FormatNumber value={proxyApplication.plan.requestsLimit} /> requests</TableCell>
                </TableRow>
                {proxyApplication.vercelProjectExternalId &&
                  <TableRow>
                    <TableCell>Vercel Project Name</TableCell>
                    <TableCell>{proxyApplication.name}</TableCell>
                  </TableRow>
                }
                {proxyApplication.vercelProjectFramework &&
                  <TableRow>
                    <TableCell>Vercel Framework</TableCell>
                    <TableCell>{proxyApplication.vercelProjectFramework}</TableCell>
                  </TableRow>
                }
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card sx={{ marginBottom: '1rem' }}>
          <CardHeader title="Getting started" />
          <CardContent>
            <Typography variant="body1">
              Make a request through this proxy using cURL:
            </Typography>
            <ExampleCurl url={proxyApplication.proxyUrl} type={proxyApplication.proxyType} />
            <Typography variant="body1">
              For language-specific instructions and examples, see our <MuiLink href={documentationUrl} target="_blank">documentation</MuiLink>.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  )
}

function useProxyApplication(id: string): [ProxyApplicationDetails | undefined, boolean] {
  const [proxyApplication, setProxyApplication] = useState<ProxyApplicationDetails>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProxyApplication = async () => {
      setIsLoading(true);
      try {
        const response = await getProxyApplication(id);
        if (response.ok) {
          setProxyApplication(await response.json());
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProxyApplication();
  }, [id]);

  return [proxyApplication, isLoading];
}

export default () => {
  const { id } = useParams();
  const [proxyApplication, isLoading] = useProxyApplication(id!);

  return (
    <DashboardPage
      title="Proxy Application Details"
      headerChildren={<Button component={RouterLink} to={`/apps/${id}/logs`}>View Logs →</Button>}
      isLoading={isLoading}
    >
      {proxyApplication ? <DetailsBody proxyApplication={proxyApplication} /> : <ErrorState errorMessage="Could not load proxy application" />}
    </DashboardPage>
  );
}
